import { scheduleHistoryReveal, scheduleRevealSelection } from "../reveal-selection";
import * as syncActions from "./actions";
import type {
  EditorRootState,
  EditorThunkAction,
} from "./types";

function afterDocumentChange(
  dispatch: (action: ReturnType<typeof syncActions.setSaveStatus>) => void,
  getState: () => EditorRootState,
): void {
  void dispatch;
  void getState;
}

function afterDocumentChangeAndReveal(
  dispatch: (action: ReturnType<typeof syncActions.setSaveStatus>) => void,
  getState: () => EditorRootState,
): void {
  afterDocumentChange(dispatch, getState);
  scheduleRevealSelection();
}

function afterHistoryNavigation(
  dispatch: (action: ReturnType<typeof syncActions.setSaveStatus>) => void,
  getState: () => EditorRootState,
  beforeDocument: EditorRootState["editor"]["document"],
): void {
  afterDocumentChange(dispatch, getState);
  scheduleHistoryReveal(
    beforeDocument,
    getState().editor.document,
    getState().editor.selection,
  );
}

export const loadDocument =
  (...args: Parameters<typeof syncActions.loadDocument>): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.loadDocument(...args));
  };

export const undo = (): EditorThunkAction => (dispatch, getState) => {
  const beforeDocument = getState().editor.document;
  dispatch(syncActions.undo());
  afterHistoryNavigation(dispatch, getState, beforeDocument);
};

export const redo = (): EditorThunkAction => (dispatch, getState) => {
  const beforeDocument = getState().editor.document;
  dispatch(syncActions.redo());
  afterHistoryNavigation(dispatch, getState, beforeDocument);
};

export const setMode =
  (mode: Parameters<typeof syncActions.setMode>[0]): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.setMode(mode));
  };

export const setSelection =
  (
    selection: Parameters<typeof syncActions.setSelection>[0],
  ): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.setSelection(selection));
  };

export const setBlockSelection =
  (
    blockSelection: Parameters<typeof syncActions.setBlockSelection>[0],
  ): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.setBlockSelection(blockSelection));
  };

export const setRowSelection =
  (
    rowSelection: Parameters<typeof syncActions.setRowSelection>[0],
  ): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.setRowSelection(rowSelection));
  };

export const setTextEditingBlock =
  (
    target: Parameters<typeof syncActions.setTextEditingBlock>[0],
  ): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.setTextEditingBlock(target));
  };

export const setTextEditReentryGuard =
  (
    guard: Parameters<typeof syncActions.setTextEditReentryGuard>[0],
  ): EditorThunkAction =>
  dispatch => {
    dispatch(syncActions.setTextEditReentryGuard(guard));
  };

export const updateSettings =
  (
    patch: Parameters<typeof syncActions.updateSettings>[0],
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.updateSettings(patch));
    afterDocumentChange(dispatch, getState);
  };

export const updateRow =
  (...args: Parameters<typeof syncActions.updateRow>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.updateRow(...args));
    afterDocumentChange(dispatch, getState);
  };

export const updateGroup =
  (...args: Parameters<typeof syncActions.updateGroup>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.updateGroup(...args));
    afterDocumentChange(dispatch, getState);
  };

export const updateColumn =
  (...args: Parameters<typeof syncActions.updateColumn>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.updateColumn(...args));
    afterDocumentChange(dispatch, getState);
  };

export const updateBlock =
  (...args: Parameters<typeof syncActions.updateBlock>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.updateBlock(...args));
    afterDocumentChange(dispatch, getState);
  };

export const insertBlock =
  (...args: Parameters<typeof syncActions.insertBlock>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.insertBlock(...args));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const moveBlock =
  (...args: Parameters<typeof syncActions.moveBlock>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveBlock(...args));
    afterDocumentChange(dispatch, getState);
  };

export const insertRowWithBlock =
  (
    ...args: Parameters<typeof syncActions.insertRowWithBlock>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.insertRowWithBlock(...args));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const moveBlockToNewRow =
  (
    ...args: Parameters<typeof syncActions.moveBlockToNewRow>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveBlockToNewRow(...args));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const insertRow =
  (...args: Parameters<typeof syncActions.insertRow>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.insertRow(...args));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const insertGroup =
  (...args: Parameters<typeof syncActions.insertGroup>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.insertGroup(...args));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const insertSnippet =
  (...args: Parameters<typeof syncActions.insertSnippet>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.insertSnippet(...args));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const moveRow =
  (...args: Parameters<typeof syncActions.moveRow>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveRow(...args));
    afterDocumentChange(dispatch, getState);
  };

export const moveTopLevelSection =
  (
    ...args: Parameters<typeof syncActions.moveTopLevelSection>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveTopLevelSection(...args));
    afterDocumentChange(dispatch, getState);
  };

export const moveContiguousTopLevelRows =
  (
    ...args: Parameters<typeof syncActions.moveContiguousTopLevelRows>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveContiguousTopLevelRows(...args));
    afterDocumentChange(dispatch, getState);
  };

export const moveContiguousGroupRows =
  (
    ...args: Parameters<typeof syncActions.moveContiguousGroupRows>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveContiguousGroupRows(...args));
    afterDocumentChange(dispatch, getState);
  };

export const moveRowInGroup =
  (...args: Parameters<typeof syncActions.moveRowInGroup>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveRowInGroup(...args));
    afterDocumentChange(dispatch, getState);
  };

export const moveRowBetweenParents =
  (
    ...args: Parameters<typeof syncActions.moveRowBetweenParents>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveRowBetweenParents(...args));
    afterDocumentChange(dispatch, getState);
  };

export const ungroup =
  (groupId: Parameters<typeof syncActions.ungroup>[0]): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.ungroup(groupId));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const groupRows =
  (rowIds: Parameters<typeof syncActions.groupRows>[0]): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.groupRows(rowIds));
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const deleteGroup =
  (...args: Parameters<typeof syncActions.deleteGroup>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.deleteGroup(...args));
    afterDocumentChange(dispatch, getState);
  };

export const setColumnWidths =
  (
    ...args: Parameters<typeof syncActions.setColumnWidths>
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.setColumnWidths(...args));
    afterDocumentChange(dispatch, getState);
  };

export const addColumn =
  (...args: Parameters<typeof syncActions.addColumn>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.addColumn(...args));
    afterDocumentChange(dispatch, getState);
  };

export const removeColumn =
  (...args: Parameters<typeof syncActions.removeColumn>): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.removeColumn(...args));
    afterDocumentChange(dispatch, getState);
  };

export const duplicateSelected =
  (): EditorThunkAction => (dispatch, getState) => {
    dispatch(syncActions.duplicateSelected());
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const deleteSelected = (): EditorThunkAction => (dispatch, getState) => {
  dispatch(syncActions.deleteSelected());
  afterDocumentChange(dispatch, getState);
};

export const moveSelected =
  (
    direction: Parameters<typeof syncActions.moveSelected>[0],
  ): EditorThunkAction =>
  (dispatch, getState) => {
    dispatch(syncActions.moveSelected(direction));
    afterDocumentChange(dispatch, getState);
  };

export const copySelectedBlocksToClipboard =
  (): EditorThunkAction => dispatch => {
    dispatch(syncActions.copySelectedBlocks());
  };

export const cutSelectedBlocksToClipboard =
  (): EditorThunkAction => (dispatch, getState) => {
    dispatch(syncActions.cutSelectedBlocks());
    afterDocumentChange(dispatch, getState);
  };

export const pasteBlocksFromEditorClipboard =
  (): EditorThunkAction => (dispatch, getState) => {
    dispatch(syncActions.pasteBlocksFromClipboard());
    afterDocumentChangeAndReveal(dispatch, getState);
  };

export const editorThunks = {
  loadDocument,
  undo,
  redo,
  setMode,
  setSelection,
  setBlockSelection,
  setRowSelection,
  setTextEditingBlock,
  setTextEditReentryGuard,
  updateSettings,
  updateRow,
  updateGroup,
  updateColumn,
  updateBlock,
  insertBlock,
  moveBlock,
  insertRowWithBlock,
  moveBlockToNewRow,
  insertRow,
  insertGroup,
  insertSnippet,
  moveRow,
  moveTopLevelSection,
  moveContiguousTopLevelRows,
  moveContiguousGroupRows,
  moveRowInGroup,
  moveRowBetweenParents,
  ungroup,
  groupRows,
  deleteGroup,
  setColumnWidths,
  addColumn,
  removeColumn,
  duplicateSelected,
  deleteSelected,
  moveSelected,
  copySelectedBlocksToClipboard,
  cutSelectedBlocksToClipboard,
  pasteBlocksFromEditorClipboard,
};

export type EditorThunks = typeof editorThunks;
